import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API_URL from "../config";
import { Box, Button, Stack, TextField, Pagination, FormControl, InputLabel, Select, MenuItem ,Table,TableHead,TableRow, TableCell, TableBody,TableContainer,Paper,Checkbox,} from '@mui/material';

const SessionHistory = () => {
  // State for storing all session records
  const [sessions, setSessions] = useState([]);
  // State for pagination: current page and rows per page
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;
  const [jumpPage, setJumpPage] = useState('');
  // State for filter controls: month and lab
  const [monthFilter, setMonthFilter] = useState("");
  const [labFilter, setLabFilter] = useState("");
  // State for storing lab options (for lab filter)
  const [labs, setLabs] = useState([]);
  const navigate = useNavigate();
  // This state holds the IDs of selected sessions
const [selectedSessionIds, setSelectedSessionIds] = useState([]);

  // On component mount, fetch session history and labs list for filtering
  useEffect(() => {
    fetchSessionHistory();
    fetchLabs();
  }, []);

  // Fetch session history from the backend and only use sessions with status "ended"
  const fetchSessionHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/lab-sessions`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      const endedSessions = response.data.filter(session => session.status === "ended" || session.status === "active")
        .sort((a, b) => new Date(b.scheduledStart) - new Date(a.scheduledStart));
      setSessions(endedSessions);
    } catch (error) {
      console.error("Error fetching session history:", error);
    }
  };

  // Fetch labs from the backend for the lab filter dropdown
  const fetchLabs = async () => {
    try {
      const response = await axios.get(`${API_URL}/lab`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      setLabs(response.data);
    } catch (error) {
      console.error("Error fetching labs:", error);
    }
  };

  // Filter sessions based on selected month and lab
  const filteredSessions = sessions.filter(session => {
    let matchesMonth = true;
    let matchesLab = true;

    // Check month filter if selected
    if (monthFilter) {
      const sessionMonth = new Date(session.scheduledStart).getMonth() + 1; // getMonth() returns 0-11
      matchesMonth = sessionMonth === parseInt(monthFilter);
    }

    // Check lab filter if selected
    if (labFilter) {
      matchesLab = session.lab && session.lab._id === labFilter;
    }
    return matchesMonth && matchesLab;
  });

  // Calculate total pages based on filtered sessions and rows per page
  const totalPages = Math.ceil(filteredSessions.length / rowsPerPage);
  const currentSessions = filteredSessions.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  // Handle page change triggered by Pagination component
  const handleChangePage = (event, value) => {
    setPage(value);
  };

  // Handle changes in the "jump to page" text field
  const handleJumpPageChange = (e) => {
    setJumpPage(e.target.value);
  };

  // When user clicks "Go", jump to the specified page if valid
  const handleJumpPage = () => {
    const pageNumber = parseInt(jumpPage, 10);
    if (!isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= totalPages) {
      setPage(pageNumber);
    }
  };

  // Handler for clearing records (invokes cleanup endpoints)
  const handleClearRecords = async () => {
    try {
      await axios.get(`${API_URL}/run-cleanup`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      await axios.get(`${API_URL}/run-cleanup-sessions`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      alert("Clear record operation successful!");
    } catch (error) {
      console.error("Error during cleanup:", error);
      alert("Clearing failed, please check the console or network request.");
    }
  };

  // Navigate to session details on session card click
  const handleSessionClick = (sessionId) => {
    navigate(`/assistant-session/${sessionId}`);
  };

  // This function toggles a single session's checkbox
const handleCheckboxChange = (sessionId) => {
  setSelectedSessionIds((prev) => {
    if (prev.includes(sessionId)) {
      return prev.filter((id) => id !== sessionId);
    } else {
      return [...prev, sessionId];
    }
  });
};

// This function handles "select all" checkboxes in the current page (or subset)
const handleSelectAllInPage = (checked, displayedSessions) => {
  if (checked) {
    const idsToAdd = displayedSessions
      .map((s) => s._id)
      .filter((id) => !selectedSessionIds.includes(id));
    setSelectedSessionIds([...selectedSessionIds, ...idsToAdd]);
  } else {
    const remaining = selectedSessionIds.filter((id) => {
      return !displayedSessions.some((ds) => ds._id === id);
    });
    setSelectedSessionIds(remaining);
  }
};

// This function downloads logs for each selected session, using session.supervisor
const handleExportSelected = async () => {
  if (selectedSessionIds.length === 0) {
    alert("No sessions selected.");
    return;
  }
  const selectedSessions = sessions.filter((ses) => selectedSessionIds.includes(ses._id));
  let successCount = 0;
  let noRecordsCount = 0;
  let errorCount = 0;
  for (const ses of selectedSessions) {
    // Create a session information string using date and location
    const sessionInfo = `${new Date(ses.scheduledStart).toLocaleDateString()} at ${ses.lab?.location || "Unknown Location"}`;
    if (!ses.supervisor) {
      console.warn("No supervisor for session:", sessionInfo);
      alert(`Session ${sessionInfo} does not have a supervisor. Skipping download for this session.`);
      continue;
    }
    try {
      const response = await axios.get(
        `${API_URL}/lab-sessions/${ses._id}/records?supervisor=${encodeURIComponent(ses.supervisor)}`,
        {
          responseType: "blob",
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        }
      );
      let fileName = "session_records.csv";
      const disposition = response.headers["content-disposition"];
      if (disposition && disposition.includes("filename=")) {
        fileName = disposition.split("filename=")[1].trim().replace(/['"]/g, "");
      }
      const blob = new Blob([response.data], { type: "text/csv" });
      const file = new File([blob], fileName, { type: "text/csv" });
      const link = document.createElement("a");
      const url = window.URL.createObjectURL(file);
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
      successCount++;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        noRecordsCount++;
        alert(`No student logs found for session ${sessionInfo}. Skipping download for this session.`);
      } else {
        errorCount++;
        console.error("Error downloading logs for session:", sessionInfo, err);
        alert(`Error downloading logs for session ${sessionInfo}.`);
      }
    }
  }
  alert(`Download completed: ${successCount} sessions downloaded, ${noRecordsCount} sessions had no records, and ${errorCount} sessions encountered errors.`);
};

return (
  <div>
    <Box
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      gap={2}
      mb={2}
    >
      <Button
        variant="contained"
        color="error"
        onClick={handleClearRecords}
        sx={{ ml: 2, display: { xs: "none", md: "inline-flex" } }}
      >
        Clear Record
      </Button>
      <FormControl variant="outlined" size="medium" sx={{ minWidth: 160 }}>
        <InputLabel id="month-filter-label">Filter by Month</InputLabel>
        <Select
          labelId="month-filter-label"
          value={monthFilter}
          label="Filter by Month"
          onChange={(e) => setMonthFilter(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {[...Array(12)].map((_, index) => (
            <MenuItem key={index + 1} value={index + 1}>
              {new Date(0, index).toLocaleString("default", { month: "long" })}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl variant="outlined" size="medium" sx={{ minWidth: 160 }}>
        <InputLabel id="lab-filter-label">Filter by Lab</InputLabel>
        <Select
          labelId="lab-filter-label"
          value={labFilter}
          label="Filter by Lab"
          onChange={(e) => setLabFilter(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {labs.map((lab) => (
            <MenuItem key={lab._id} value={lab._id}>
              {lab.name} ({lab.location})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>

    <TableContainer
      component={Paper}
      sx={{ mt: 2, maxHeight: 500, width: "90%", mx: "auto" }}
    >
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell align="center" padding="checkbox">
              <Checkbox
                checked={
                  currentSessions.length > 0 &&
                  currentSessions.every((sess) =>
                    selectedSessionIds.includes(sess._id)
                  )
                }
                indeterminate={
                  currentSessions.some((sess) =>
                    selectedSessionIds.includes(sess._id)
                  ) &&
                  currentSessions.some((sess) => !selectedSessionIds.includes(sess._id))
                }
                onChange={(e) =>
                  handleSelectAllInPage(e.target.checked, currentSessions)
                }
              />
            </TableCell>
            <TableCell align="center">Date</TableCell>
            <TableCell align="center">Lab Name</TableCell>
            <TableCell align="center">Location</TableCell>
            <TableCell
              align="center"
              sx={{ display: { xs: "none", md: "table-cell" } }}
            >
              Supervisor
            </TableCell>
            <TableCell
              align="center"
              sx={{ display: { xs: "none", md: "table-cell" } }}
            >
              Time Range
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {currentSessions.length > 0 ? (
            currentSessions.map((session) => {
              const sessionDate = new Date(session.scheduledStart).toLocaleDateString();
              const startTime = new Date(session.scheduledStart).toLocaleString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false
              });
              const endTime = new Date(session.scheduledEnd).toLocaleString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false
              });
              return (
                <TableRow
                  hover
                  key={session._id}
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleSessionClick(session._id)}
                >
                  <TableCell
                    align="center"
                    padding="checkbox"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={selectedSessionIds.includes(session._id)}
                      onChange={() => handleCheckboxChange(session._id)}
                    />
                  </TableCell>
                  <TableCell align="center">{sessionDate}</TableCell>
                  <TableCell align="center">
                    {session.lab?.name || "Unknown Lab"}
                  </TableCell>
                  <TableCell align="center">
                    {session.lab?.location || "Unknown"}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ display: { xs: "none", md: "table-cell" } }}
                  >
                    {session.supervisor || "N/A"}
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ display: { xs: "none", md: "table-cell" } }}
                  >
                    {startTime} - {endTime}
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center">
                No sessions found for the selected filters.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>

    <Stack
      spacing={2}
      direction={{ xs: "column", md: "row" }}
      justifyContent="center"
      alignItems="center"
      sx={{ mt: 2 }}
    >
      <Button
        variant="contained"
        onClick={handleExportSelected}
        sx={{ display: {  md: "inline-flex" } }}
      >
        Export Selected
      </Button>
      <Pagination
        count={totalPages}
        page={page}
        onChange={handleChangePage}
        color="primary"
      />
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1}
        alignItems="center"
      >
        <TextField
          label="Jump to page"
          variant="outlined"
          size="small"
          value={jumpPage}
          onChange={handleJumpPageChange}
        />
        <Button variant="contained" onClick={handleJumpPage}>
          Go
        </Button>
      </Stack>
    </Stack>
  </div>
);

};

export default SessionHistory;
